<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ArrowLeft, ArrowRight, Pencil, Plus, RotateCcw, Search } from 'lucide-vue-next'
import { customersApi, paymentsApi, workOrdersApi, type PaymentDirection, type PaymentRecord } from '@ck/api-client'
import { formatCurrency, formatCurrencyInput, formatDate, formatDateInput, normalizeDateOnly, parseCurrencyInput } from '@ck/domain'
import { CkAlert, CkButton, CkField, CkModal, CkStatTile, CkTable } from '@ck/ui'
import { apiErrorMessage, isConflictError, totalPages } from '../lib/operational'
import { resolvePaymentWorkOrder } from '../lib/payment-form'

const pageSize = 20
const queryClient = useQueryClient()
const directionOptions: Array<{ value: PaymentDirection | ''; label: string }> = [
  { value: '', label: 'Todas as direções' },
  { value: 'in', label: 'Entrada' },
  { value: 'out', label: 'Saída' }
]

const filters = reactive({ customerId: '', customerSearch: '', dateFrom: '', dateTo: '', direction: '' as PaymentDirection | '' })
const activeFilters = ref<{ customerId: string; dateFrom: string; dateTo: string; direction?: PaymentDirection }>({ customerId: '', dateFrom: '', dateTo: '' })
const page = ref(1)
const customerSearchQuery = useQuery(computed(() => ({
  queryKey: ['payment-customer-search', filters.customerSearch],
  queryFn: () => customersApi.list({ q: filters.customerSearch.trim(), page: 1, pageSize: 8 }),
  enabled: filters.customerSearch.trim().length >= 2
})))
const filterQuery = computed(() => ({ ...activeFilters.value, page: page.value, pageSize }))
const summaryQuery = useQuery(computed(() => ({
  queryKey: ['payments-summary', activeFilters.value],
  queryFn: () => paymentsApi.summary(activeFilters.value)
})))
const paymentsQuery = useQuery(computed(() => ({
  queryKey: ['payments', filterQuery.value],
  queryFn: () => paymentsApi.list(filterQuery.value)
})))

const result = computed(() => paymentsQuery.data.value)
const pages = computed(() => totalPages(result.value?.total ?? 0, pageSize))
const customerOptions = computed(() => customerSearchQuery.data.value?.items ?? [])
const selectedCustomerName = ref('')

function applyFilters() {
  activeFilters.value = {
    customerId: filters.customerId,
    dateFrom: normalizeDateOnly(filters.dateFrom) ?? '',
    dateTo: normalizeDateOnly(filters.dateTo) ?? '',
    direction: filters.direction || undefined
  }
  page.value = 1
}

function selectCustomer(id: string, name: string) {
  filters.customerId = id
  selectedCustomerName.value = name
  filters.customerSearch = name
}

function clearCustomer() {
  filters.customerId = ''
  filters.customerSearch = ''
  selectedCustomerName.value = ''
}

function setPage(next: number) {
  if (next >= 1 && next <= pages.value) page.value = next
}

const showPaymentModal = ref(false)
const editingPayment = ref<PaymentRecord | null>(null)
const paymentForm = reactive({ direction: 'in' as PaymentDirection, amount: '', occurredOn: '', description: '', category: '', paymentMethod: '', customerId: '', workOrderId: '', workOrderNumber: '' })
const paymentErrors = ref<Record<string, string>>({})
const paymentConflict = ref(false)
const paymentError = ref<string | null>(null)
const workOrderSearchQuery = useQuery(computed(() => ({
  queryKey: ['payment-work-order-search', paymentForm.workOrderNumber],
  queryFn: () => workOrdersApi.list({ q: paymentForm.workOrderNumber.trim(), status: 'all', page: 1, pageSize: 8 }),
  enabled: showPaymentModal.value && paymentForm.workOrderNumber.trim().length >= 2
})))
const workOrderOptions = computed(() => workOrderSearchQuery.data.value?.items ?? [])

function resetPaymentForm(payment: PaymentRecord | null = null) {
  editingPayment.value = payment
  paymentForm.direction = payment?.direction ?? 'in'
  paymentForm.amount = payment ? formatCurrency(payment.amount) : ''
  paymentForm.occurredOn = payment ? formatDateInput(payment.occurredOn) : formatDateInput(new Date().toISOString().slice(0, 10))
  paymentForm.description = payment?.description ?? ''
  paymentForm.category = payment?.category ?? ''
  paymentForm.paymentMethod = payment?.paymentMethod ?? ''
  paymentForm.customerId = payment?.customerId ?? ''
  paymentForm.workOrderId = payment?.workOrderId ?? ''
  paymentForm.workOrderNumber = payment?.workOrderNumber ?? ''
  paymentErrors.value = {}
  paymentConflict.value = false
  paymentError.value = null
  if (payment?.customerName) {
    filters.customerSearch = payment.customerName
    selectedCustomerName.value = payment.customerName
  }
  showPaymentModal.value = true
}

function updateWorkOrderNumber(value: string) {
  paymentForm.workOrderNumber = value
  const currentNumber = editingPayment.value?.workOrderNumber ?? ''
  if (value.trim().toUpperCase() !== currentNumber.trim().toUpperCase()) paymentForm.workOrderId = ''
}

function selectWorkOrder(order: { id: string; number: string }) {
  paymentForm.workOrderNumber = order.number
  paymentForm.workOrderId = order.id
  paymentErrors.value.workOrderNumber = ''
}

function clearWorkOrder() {
  paymentForm.workOrderId = ''
  paymentForm.workOrderNumber = ''
}

const savePaymentMutation = useMutation({
  mutationFn: () => {
    const amount = parseCurrencyInput(paymentForm.amount)
    const occurredOn = normalizeDateOnly(paymentForm.occurredOn)
    if (!amount || amount <= 0) throw new Error('Informe um valor maior que zero.')
    if (!occurredOn) throw new Error('Informe uma data válida.')
    const payload = {
      direction: paymentForm.direction,
      amount,
      occurredOn,
      description: paymentForm.description.trim(),
      category: paymentForm.category.trim() || null,
      paymentMethod: paymentForm.paymentMethod.trim() || null,
      customerId: paymentForm.customerId || null,
      workOrderId: paymentForm.workOrderId.trim() || null
    }
    return editingPayment.value
      ? paymentsApi.update(editingPayment.value.id, { ...payload, expectedVersion: editingPayment.value.version })
      : paymentsApi.create(payload)
  },
  onSuccess: async () => {
    showPaymentModal.value = false
    await invalidatePayments()
  },
  onError: (error: unknown) => {
    paymentConflict.value = isConflictError(error)
    paymentError.value = paymentConflict.value ? 'Este lançamento foi alterado por outra pessoa. Recarregue os dados antes de salvar.' : apiErrorMessage(error, 'Não foi possível salvar o lançamento.')
  }
})

function savePayment() {
  paymentErrors.value = {}
  if (!paymentForm.description.trim()) paymentErrors.value.description = 'Informe a descrição.'
  if (parseCurrencyInput(paymentForm.amount) === null || (parseCurrencyInput(paymentForm.amount) ?? 0) <= 0) paymentErrors.value.amount = 'Informe um valor maior que zero.'
  if (!normalizeDateOnly(paymentForm.occurredOn)) paymentErrors.value.occurredOn = 'Informe uma data válida.'
  const workOrderSelection = resolvePaymentWorkOrder(
    paymentForm.workOrderNumber,
    paymentForm.workOrderId,
    workOrderOptions.value,
    editingPayment.value?.workOrderNumber ?? null
  )
  if (workOrderSelection.error) paymentErrors.value.workOrderNumber = workOrderSelection.error
  else paymentForm.workOrderId = workOrderSelection.id ?? ''
  if (Object.keys(paymentErrors.value).length > 0) return
  paymentError.value = null
  savePaymentMutation.mutate()
}

const paymentToReverse = ref<PaymentRecord | null>(null)
const reversalReason = ref('')
const reversalError = ref<string | null>(null)
const reverseMutation = useMutation({
  mutationFn: () => {
    const payment = paymentToReverse.value
    if (!payment) throw new Error('Lançamento não selecionado.')
    return paymentsApi.reverse(payment.id, { reason: reversalReason.value.trim(), expectedVersion: payment.version })
  },
  onSuccess: async () => {
    paymentToReverse.value = null
    reversalReason.value = ''
    reversalError.value = null
    await invalidatePayments()
  },
  onError: (error: unknown) => {
    reversalError.value = isConflictError(error) ? 'O lançamento mudou desde a abertura desta janela. Recarregue a página.' : apiErrorMessage(error, 'Não foi possível estornar o lançamento.')
  }
})

function openReversal(payment: PaymentRecord) {
  paymentToReverse.value = payment
  reversalReason.value = ''
  reversalError.value = null
}

async function invalidatePayments() {
  await queryClient.invalidateQueries({ queryKey: ['payments'] })
  await queryClient.invalidateQueries({ queryKey: ['payments-summary'] })
}

function directionLabel(direction: PaymentDirection) { return direction === 'in' ? 'Entrada' : 'Saída' }
</script>

<template>
  <div class="page">
    <div class="page-head">
      <div><span class="ck-eyebrow">FINANCEIRO</span><h1>Pagamentos</h1><p>Entradas e saídas registradas com trilha de auditoria.</p></div>
      <CkButton @click="resetPaymentForm()"><Plus :size="17" aria-hidden="true"/>Lançar pagamento</CkButton>
    </div>

    <CkAlert v-if="summaryQuery.isError.value || paymentsQuery.isError.value" tone="error">Não foi possível carregar os pagamentos. Tente novamente.</CkAlert>
    <section class="summary" aria-label="Resumo financeiro">
      <CkStatTile :value="formatCurrency(summaryQuery.data.value?.totalIn)" label="Total de entradas" tone="ok"/>
      <CkStatTile :value="formatCurrency(summaryQuery.data.value?.totalOut)" label="Total de saídas" tone="fail"/>
      <CkStatTile :value="formatCurrency(summaryQuery.data.value?.balance)" label="Saldo" :tone="(summaryQuery.data.value?.balance ?? 0) >= 0 ? 'accent' : 'fail'"/>
    </section>

    <section class="filters" aria-label="Filtros de pagamentos">
      <div class="customer-filter">
        <CkField v-model="filters.customerSearch" label="Cliente" placeholder="Nome do cliente" inputmode="search" autocomplete="off" :maxlength="120"/>
        <ul v-if="customerOptions.length && !filters.customerId" class="customer-options">
          <li v-for="customer in customerOptions" :key="customer.id"><button type="button" @click="selectCustomer(customer.id, customer.name)">{{ customer.name }}<small>{{ customer.email }}</small></button></li>
        </ul>
        <button v-if="filters.customerId" type="button" class="selected-filter" @click="clearCustomer">{{ selectedCustomerName || 'Cliente selecionado' }} <span aria-hidden="true">×</span></button>
      </div>
      <CkField :model-value="filters.dateFrom" label="De" placeholder="dd/MM/yyyy" inputmode="numeric" :maxlength="10" @update:model-value="filters.dateFrom = formatDateInput($event)"/>
      <CkField :model-value="filters.dateTo" label="Até" placeholder="dd/MM/yyyy" inputmode="numeric" :maxlength="10" @update:model-value="filters.dateTo = formatDateInput($event)"/>
      <label class="select-field"><span>Direção</span><select v-model="filters.direction"><option v-for="option in directionOptions" :key="option.value" :value="option.value">{{ option.label }}</option></select></label>
      <CkButton variant="secondary" @click="applyFilters"><Search :size="16" aria-hidden="true"/>Filtrar</CkButton>
    </section>

    <div class="table-wrap">
      <CkTable
        :columns="[
          { key: 'date', label: 'Data', width: '110px' },
          { key: 'description', label: 'Descrição', width: '1.5fr' },
          { key: 'customer', label: 'Cliente', width: '1.2fr' },
          { key: 'direction', label: 'Direção', width: '110px' },
          { key: 'amount', label: 'Valor', width: '130px', align: 'right' },
          { key: 'status', label: 'Status', width: '120px' },
          { key: 'actions', label: '', width: '116px', align: 'right' }
        ]" :rows="result?.items ?? []" :row-key="(row) => row.id" empty-message="Nenhum pagamento encontrado."
      >
        <template #default="{ item }">
          <span class="ck-code">{{ formatDate(item.occurredOn) }}</span>
          <span><strong>{{ item.description }}</strong><small v-if="item.workOrderNumber" class="ck-code">{{ item.workOrderNumber }}</small></span>
          <span>{{ item.customerName || 'Sem cliente' }}</span>
          <span class="direction" :class="`direction--${item.direction}`">{{ directionLabel(item.direction) }}</span>
          <span class="amount" :class="`amount--${item.direction}`">{{ item.direction === 'out' ? '−' : '+' }} {{ formatCurrency(item.amount) }}</span>
          <span class="status" :class="`status--${item.status}`">{{ item.status === 'reversed' ? 'Estornado' : 'Lançado' }}</span>
          <span class="row-actions">
            <button v-if="item.status !== 'reversed'" type="button" :aria-label="`Editar pagamento ${item.description}`" @click="resetPaymentForm(item)"><Pencil :size="14" aria-hidden="true"/></button>
            <button v-if="item.status !== 'reversed'" type="button" :aria-label="`Estornar pagamento ${item.description}`" @click="openReversal(item)"><RotateCcw :size="14" aria-hidden="true"/></button>
            <span v-else class="audit">Auditado</span>
          </span>
        </template>
      </CkTable>
    </div>
    <nav v-if="pages > 1" class="pagination" aria-label="Paginação de pagamentos">
      <CkButton variant="ghost" :disabled="page <= 1" aria-label="Página anterior" @click="setPage(page - 1)"><ArrowLeft :size="16"/>Anterior</CkButton>
      <span>Página <strong>{{ page }}</strong> de <strong>{{ pages }}</strong></span>
      <CkButton variant="ghost" :disabled="page >= pages" aria-label="Próxima página" @click="setPage(page + 1)">Próxima<ArrowRight :size="16"/></CkButton>
    </nav>

    <CkModal v-if="showPaymentModal" :title="editingPayment ? 'Editar pagamento' : 'Lançar pagamento'" @close="showPaymentModal = false">
      <form class="payment-form" @submit.prevent="savePayment">
        <CkAlert v-if="paymentError" tone="error">{{ paymentError }}</CkAlert>
        <div class="form-grid">
          <label class="select-field"><span>Direção</span><select v-model="paymentForm.direction"><option value="in">Entrada</option><option value="out">Saída</option></select></label>
          <CkField :model-value="paymentForm.amount" label="Valor" type="text" placeholder="R$ 0,00" inputmode="decimal" :maxlength="20" :error="paymentErrors.amount" @update:model-value="paymentForm.amount = formatCurrencyInput($event)"/>
          <CkField :model-value="paymentForm.occurredOn" label="Data" placeholder="dd/MM/yyyy" inputmode="numeric" :maxlength="10" :error="paymentErrors.occurredOn" @update:model-value="paymentForm.occurredOn = formatDateInput($event)"/>
          <div class="work-order-field">
            <CkField :model-value="paymentForm.workOrderNumber" label="Número da OS (opcional)" placeholder="Ex.: OS-2026-0001" inputmode="text" :maxlength="20" :error="paymentErrors.workOrderNumber" autocomplete="off" @update:model-value="updateWorkOrderNumber"/>
            <p v-if="!paymentForm.workOrderId" class="field-hint">Digite o número e selecione a ordem correspondente.</p>
            <ul v-if="workOrderOptions.length && paymentForm.workOrderNumber.trim().length >= 2 && !paymentForm.workOrderId" class="work-order-options">
              <li v-for="order in workOrderOptions" :key="order.id"><button type="button" @click="selectWorkOrder(order)"><strong>{{ order.number }}</strong><small>{{ order.customer }} · {{ order.vehicle.brand }} {{ order.vehicle.model }}</small></button></li>
            </ul>
            <button v-if="paymentForm.workOrderId" type="button" class="selected-work-order" @click="clearWorkOrder"><span>{{ paymentForm.workOrderNumber }}</span><span aria-hidden="true">×</span></button>
          </div>
        </div>
        <div class="form-grid">
          <CkField v-model="paymentForm.category" label="Categoria (opcional)" placeholder="Ex.: peças, serviço" :maxlength="80"/>
          <CkField v-model="paymentForm.paymentMethod" label="Método (opcional)" placeholder="Pix, cartão, dinheiro" :maxlength="60"/>
        </div>
        <CkField v-model="paymentForm.description" label="Descrição" placeholder="Ex.: entrada da restauração" inputmode="text" :maxlength="180" required :error="paymentErrors.description"/>
        <div class="form-actions"><CkButton type="button" variant="ghost" @click="showPaymentModal = false">Cancelar</CkButton><CkButton type="submit" :disabled="savePaymentMutation.isPending.value">{{ savePaymentMutation.isPending.value ? 'Salvando…' : 'Salvar lançamento' }}</CkButton></div>
      </form>
    </CkModal>

    <CkModal v-if="paymentToReverse" title="Estornar pagamento" @close="paymentToReverse = null">
      <p class="modal-copy">O estorno mantém o lançamento original e registra o motivo e o operador para auditoria.</p>
      <p class="original-payment"><strong>{{ paymentToReverse.description }}</strong><span>{{ formatCurrency(paymentToReverse.amount) }} · {{ formatDate(paymentToReverse.occurredOn) }}</span></p>
      <CkField v-model="reversalReason" label="Motivo do estorno" placeholder="Ex.: lançamento duplicado" inputmode="text" :maxlength="180" required/>
      <CkAlert v-if="reversalError" tone="error">{{ reversalError }}</CkAlert>
      <div class="form-actions"><CkButton type="button" variant="ghost" @click="paymentToReverse = null">Voltar</CkButton><CkButton type="button" variant="secondary" :disabled="reverseMutation.isPending.value || !reversalReason.trim()" @click="reverseMutation.mutate()">{{ reverseMutation.isPending.value ? 'Estornando…' : 'Confirmar estorno' }}</CkButton></div>
    </CkModal>
  </div>
</template>

<style scoped>
.page-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); margin-bottom: var(--space-5); }
.ck-eyebrow { color: var(--ck-amber-400); }
h1 { margin: 5px 0 4px; font-size: var(--fs-2xl); font-weight: var(--fw-extrabold); }
.page-head p { margin: 0; color: var(--color-text-muted); font-size: var(--fs-sm); }
.summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-4); margin-bottom: var(--space-5); }
.filters { display: grid; grid-template-columns: 1.5fr 150px 150px 180px auto; align-items: end; gap: var(--space-3); margin-bottom: var(--space-4); padding: var(--space-4); border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.filters :deep(.ck-field) { margin-bottom: 0; }
.customer-filter { position: relative; min-width: 0; }
.customer-options { position: absolute; z-index: 5; top: calc(100% + 4px); right: 0; left: 0; display: grid; gap: 2px; margin: 0; padding: var(--space-2); border: 1px solid var(--color-border-strong); border-radius: var(--radius-md); background: var(--color-surface); box-shadow: var(--shadow-lg); list-style: none; }
.customer-options button { display: flex; flex-direction: column; align-items: flex-start; width: 100%; padding: var(--space-2) var(--space-3); border: 0; border-radius: var(--radius-sm); background: transparent; color: var(--color-text); text-align: left; cursor: pointer; }
.customer-options button:hover, .customer-options button:focus-visible { background: var(--color-surface-muted); }
.customer-options small { color: var(--color-text-muted); }
.selected-filter { display: block; max-width: 100%; margin-top: var(--space-1); padding: 3px 9px; overflow: hidden; border: 1px solid var(--color-border); border-radius: var(--radius-full); background: var(--color-surface-muted); color: var(--color-text-muted); font-size: var(--fs-xs); text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }
.select-field { display: block; margin-bottom: var(--space-4); }
.select-field span { display: block; margin-bottom: var(--space-1); color: var(--color-text-muted); font-size: var(--fs-sm); font-weight: var(--fw-semibold); }
.select-field select { width: 100%; min-height: 44px; padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); color: var(--color-text); }
.table-wrap { overflow-x: auto; }
.table-wrap :deep(.ck-table) { min-width: 980px; }
.table-wrap small { display: block; margin-top: 2px; color: var(--color-text-faint); font-size: var(--fs-2xs); }
.direction, .status { display: inline-flex; width: fit-content; padding: 4px 9px; border-radius: var(--radius-full); font-size: var(--fs-xs); font-weight: var(--fw-bold); }
.direction--in { background: var(--ck-ok-bg); color: var(--ck-ok-text); }
.direction--out { background: var(--ck-warn-bg); color: var(--ck-warn-text); }
.status--posted { background: var(--ck-info-bg); color: var(--ck-info-text); }
.status--reversed { background: var(--ck-fail-bg); color: var(--ck-fail-text); }
.amount { font-family: var(--font-mono); font-size: var(--fs-xs); text-align: right; }
.amount--in { color: var(--ck-ok-500); }
.amount--out { color: var(--ck-warn-600); }
.row-actions { display: inline-flex; align-items: center; justify-content: flex-end; gap: var(--space-1); width: 100%; }
.row-actions button { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: transparent; color: var(--color-text-muted); cursor: pointer; }
.row-actions button:hover, .row-actions button:focus-visible { border-color: var(--ck-orange-500); color: var(--ck-orange-500); }
.audit { color: var(--color-text-faint); font-size: var(--fs-2xs); }
.pagination { display: flex; align-items: center; justify-content: center; gap: var(--space-4); margin-top: var(--space-5); color: var(--color-text-muted); font-size: var(--fs-sm); }
.payment-form { display: grid; gap: var(--space-2); }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 var(--space-4); }
.work-order-field { position: relative; min-width: 0; }
.field-hint { margin: 3px 0 0; color: var(--color-text-faint); font-size: var(--fs-2xs); }
.work-order-options { position: absolute; z-index: 6; top: calc(100% - 2px); right: 0; left: 0; display: grid; gap: 2px; max-height: 230px; margin: 0; padding: var(--space-2); overflow-y: auto; border: 1px solid var(--color-border-strong); border-radius: var(--radius-md); background: var(--color-surface); box-shadow: var(--shadow-lg); list-style: none; }
.work-order-options button { display: flex; flex-direction: column; align-items: flex-start; width: 100%; gap: 2px; padding: var(--space-2) var(--space-3); border: 0; border-radius: var(--radius-sm); background: transparent; color: var(--color-text); text-align: left; cursor: pointer; }
.work-order-options button:hover, .work-order-options button:focus-visible { background: var(--color-surface-muted); }
.work-order-options small { color: var(--color-text-muted); font-size: var(--fs-2xs); }
.selected-work-order { display: flex; align-items: center; justify-content: space-between; width: 100%; gap: var(--space-2); margin-top: var(--space-1); padding: 5px 9px; overflow: hidden; border: 1px solid var(--color-border); border-radius: var(--radius-full); background: var(--color-surface-muted); color: var(--color-text-muted); font-size: var(--fs-xs); text-align: left; cursor: pointer; }
.selected-work-order span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.form-actions { display: flex; justify-content: flex-end; gap: var(--space-2); margin-top: var(--space-3); }
.modal-copy { margin: 0 0 var(--space-4); color: var(--color-text-muted); font-size: var(--fs-sm); }
.original-payment { display: flex; flex-direction: column; gap: 3px; margin: 0 0 var(--space-4); padding: var(--space-3); border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-surface-muted); }
.original-payment span { color: var(--color-text-muted); font-family: var(--font-mono); font-size: var(--fs-xs); }
@media (max-width: 1000px) { .filters { grid-template-columns: 1fr 1fr; } .customer-filter { grid-column: span 2; } }
@media (max-width: 620px) {
  .page-head { flex-direction: column; }
  .page-head :deep(.ck-button) { width: 100%; }
  .summary { grid-template-columns: 1fr; }
  .filters { grid-template-columns: 1fr; }
  .customer-filter { grid-column: auto; }
  .filters :deep(.ck-button) { width: 100%; }
  .form-grid { grid-template-columns: 1fr; }
  .form-actions { flex-wrap: wrap; }
  .form-actions :deep(.ck-button) { flex: 1; }
}
</style>
